import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Box, IconButton } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Routes, { DELIVERY_ID_PARAM } from 'routes';
import { useDeliveries } from 'api/hooks/useDeliveries';
import { TableTypes } from 'components/ActionItemsPage/ActionItemsPage';
import { CheckInDeliveryDialog } from 'components/CheckInDeliveryDialog/CheckInDeliveryDialog';
import DeliveryList from 'components/DeliveryListPage/components/DeliveryList/DeliveryList';
import { addBusinessDays, formatDateToYYYYMMDD } from 'utils/helpers/DateTimeFormat.helpers';
import { useActionItemsFilters } from './hooks/useActionItemsFilters';
import { HistoricalSwitch } from 'api/models/Deliveries';
import { useDeliveryClickHandler } from 'hooks/useDeliveryClickHandler';

// Current date and time in milliseconds
const dateNow = new Date();

// Calculate the timestamps for 72 hours before and after the current date
const beginningOfTime = 0;
const dateNowPlus24Hours = addBusinessDays(dateNow, 1);

// Format the dates to 'YYYY-MM-DD'
const formattedBeginningOfTime = formatDateToYYYYMMDD(beginningOfTime);
const formattedDateNowPlus24Hours = formatDateToYYYYMMDD(dateNowPlus24Hours);

type OverdueCheckInDeliveriesTableProps = {
  expandedTable: TableTypes;
  handleToggleExpand: (type: TableTypes) => void;
};

const OverdueCheckInDeliveriesTable = ({ expandedTable, handleToggleExpand }: OverdueCheckInDeliveriesTableProps) => {
  const location = useLocation();
  const isPastDeliveries = location.pathname === Routes.PastDeliveries.path;

  const [filters, _setFilters] = useActionItemsFilters();

  // This is any delivery that is in 'Received' or 'Partially Checked in' status, where the surgery date is within 24 hours (the next day).

  const { data: overdueCheckInDeliveries, isLoading: overdueCheckInDeliveriesLoading } = useDeliveries({
    ...filters,
    status: 'OVERDUE_FOR_CHECK_IN',
    lower_surgery_date: formattedBeginningOfTime,
    upper_surgery_date: formattedDateNowPlus24Hours,
    historical: HistoricalSwitch.Active,
  });

  const { activeDeliveryForCheckInDeliveryDialog, handleDeliveryClick, setActiveDeliveryForCheckInDeliveryDialog } =
    useDeliveryClickHandler();

  const navigate = useNavigate();

  const handleRowClick = useCallback(
    async (deliveryId: number) => {
      const delivery = overdueCheckInDeliveries?.result.find(
        deliverySummary => deliverySummary.deliveryId === deliveryId,
      );

      if (!delivery) {
        throw new Error('Delivery not found');
      }

      handleDeliveryClick(deliveryId, delivery.status, !delivery.surgeryReference || !delivery.patientInitials);
    },
    [overdueCheckInDeliveries?.result, handleDeliveryClick],
  );

  const pageSize = overdueCheckInDeliveries?.metadata.count || 0;
  const pageSizeOptions = useMemo(() => [pageSize], [pageSize]);

  return (
    <>
      <Box
        sx={{
          backgroundColor: '#cbfda7',
          height: '50px',
          color: '#52c41a',
          fontWeight: 'medium',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: '20px',
          padding: '5px',
        }}
      >
        <p style={{ margin: 0, paddingLeft: '10px', fontSize: '20px' }}>
          There are{' '}
          <b>
            <u style={{ fontSize: '25px', padding: '5px' }}>{overdueCheckInDeliveries?.metadata.total || 'no'}</u>
          </b>{' '}
          Deliveries to be Checked In {overdueCheckInDeliveries?.metadata.total == 0 && 'today'}
        </p>
        <div>
          {overdueCheckInDeliveries?.metadata.total !== 0 && (
            <IconButton
              name="clear"
              title="Clear"
              sx={{ paddingRight: 0 }}
              onClick={() => handleToggleExpand(TableTypes.CHECK_IN_OVERDUE)}
              style={{ paddingRight: '10px' }}
            >
              {expandedTable == TableTypes.CHECK_IN_OVERDUE ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
            </IconButton>
          )}
        </div>
      </Box>
      {overdueCheckInDeliveries?.metadata.total !== 0 &&
        (expandedTable == TableTypes.CHECK_IN_OVERDUE || expandedTable == TableTypes.ALL) && (
          <DeliveryList
            sx={{ flex: 1, overflow: 'hidden' }} // This ensures the list fills up available vertical space
            isActionItemsTable={true}
            data={overdueCheckInDeliveries?.result || []}
            numPages={1}
            curPage={1}
            pageSizeOptions={pageSizeOptions}
            pageSize={pageSize}
            isPastDeliveries={isPastDeliveries}
            loading={overdueCheckInDeliveriesLoading}
            onRowClick={handleRowClick}
            tableType={TableTypes.CHECK_IN_OVERDUE}
          />
        )}

      {activeDeliveryForCheckInDeliveryDialog && (
        <CheckInDeliveryDialog
          deliveryId={activeDeliveryForCheckInDeliveryDialog.toString()}
          onClose={() => {
            setActiveDeliveryForCheckInDeliveryDialog(null);
          }}
          onCancel={() => {
            // we still want to open the delivery but not the check in workflow
            navigate(Routes.CheckIn.path.replace(DELIVERY_ID_PARAM, activeDeliveryForCheckInDeliveryDialog.toString()));
            setActiveDeliveryForCheckInDeliveryDialog(null);
          }}
        />
      )}
    </>
  );
};

export default OverdueCheckInDeliveriesTable;
